import Swal from 'sweetalert2';
import { downloadQuotePDF } from './pdfGenerator';

/**
 * Normaliza un número telefónico al formato internacional de WhatsApp (E.164 sin signo +).
 * Especializado para números de Argentina (código país 54 + prefijo móvil 9).
 * 
 * Ejemplos:
 * - "11-4567-8901" -> "5491145678901"
 * - "011 15 4567 8901" -> "5491145678901"
 * - "+54 9 11 4567 8901" -> "5491145678901"
 * - "541145678901" -> "5491145678901"
 * - "351 15 6789012" -> "5493516789012"
 */
export const normalizeWhatsAppPhone = (rawPhone) => {
    if (!rawPhone || typeof rawPhone !== 'string') return '';

    // 1. Quitar todos los caracteres no numéricos
    let digits = rawPhone.replace(/\D/g, '');
    if (!digits) return '';

    // 2. Si empieza con 0 (código de área interurbano nacional), quitar el 0
    if (digits.startsWith('0')) {
        digits = digits.substring(1);
    }

    // 3. Si ya tiene prefijo internacional 54
    if (digits.startsWith('54')) {
        // Si no tiene el 9 de móvil (ej: 541145678901), agregar el 9 después de 54
        if (!digits.startsWith('549')) {
            digits = '549' + digits.substring(2);
        }
    } else {
        // Si no tiene 54, asumimos número nacional de Argentina y agregamos 549
        // Manejar el caso de que tenga "15" en el medio para celulares (ej: 111545678901)
        if (digits.startsWith('1115') && digits.length === 12) {
            digits = '11' + digits.substring(4);
        }
        digits = '549' + digits;
    }

    return digits;
};

/**
 * Formatea un número monetario a formato moneda Argentina ($ 1.234,56)
 */
const formatMoney = (val) => {
    const num = parseFloat(val) || 0;
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
};

/**
 * Formateador GENÉRICO de mensaje de WhatsApp para cualquier comprobante del sistema
 * (Presupuestos, Facturas, Remitos, Recibos, etc.).
 * Utiliza formato Markdown nativo de WhatsApp, limpio y compatible con todos los dispositivos.
 */
export const formatDocumentWhatsAppMessage = ({
    docType = 'Presupuesto',
    docNumber = '',
    clientName = '',
    date = '',
    sellerName = '',
    companyName = 'SISGECOM',
    items = [],
    total = 0,
    notes = ''
}) => {
    const divider = '────────────────────────';
    const clientTitle = clientName && clientName.trim() !== 'Cliente General' ? clientName.trim() : 'Estimado Cliente';

    let msg = `*${companyName.toUpperCase()}*\n`;
    msg += `*${docType.toUpperCase()} COMERCIAL*\n`;
    msg += `${divider}\n`;
    msg += `*Cliente:* ${clientTitle}\n`;
    
    if (docNumber) {
        msg += `*Comprobante:* ${docNumber}\n`;
    }
    if (date) {
        msg += `*Fecha:* ${date}\n`;
    }
    if (sellerName) {
        msg += `*Vendedor:* ${sellerName}\n`;
    }

    // Detalle de renglones / artículos
    if (items && items.length > 0) {
        msg += `${divider}\n`;
        msg += `*Detalle de Artículos:*\n`;
        items.forEach((it) => {
            const cant = parseFloat(it.q_artic || it.quantity || 1);
            const cantStr = cant % 1 === 0 ? cant.toString() : cant.toFixed(2);
            const cod = (it.c_artic || it.codigo || '').trim();
            const desc = (it.d_artic || it.descripcion || it.d_obser || cod).trim();
            const precioUnit = parseFloat(it.i_unita || it.i_lista || it.i_publi || it.i_campre || it.price || 0);
            const descPorc = parseFloat(it.p_desc2 || it.discount || 0);
            const totItem = (it.i_venta && parseFloat(it.i_venta) > 0) ? parseFloat(it.i_venta) : (precioUnit * cant * (1 - descPorc / 100));

            msg += `• *${cantStr}* x ${desc}: *${formatMoney(totItem)}*\n`;
        });
    }

    // Total final destacado
    msg += `${divider}\n`;
    msg += `*TOTAL FINAL: ${formatMoney(total)}*\n`;
    msg += `${divider}\n`;

    if (notes && notes.trim()) {
        msg += `*Nota:* ${notes.trim()}\n\n`;
    }

    msg += `_Los precios expresados están sujetos a modificaciones sin previo aviso y a disponibilidad de stock al momento de la confirmación._`;

    return msg;
};

/**
 * Construye el mensaje de WhatsApp para un Presupuesto comercial.
 */
export const formatQuoteWhatsAppMessage = (quote, user, sellers = []) => {
    if (!quote) return '';

    const comprType = quote.c_compr ? quote.c_compr.trim() : 'PRE';
    const docNumber = quote.n_presu 
        ? `${comprType}-${String(quote.n_presu).padStart(8, '0')}` 
        : `PRE-${String(quote.id || '0').padStart(8, '0')}`;

    const clientName = quote.d_razon || 'Cliente';
    const companyName = user?.empresa_activa?.nombre || 'SISGECOM';

    // Resolver vendedor
    let sellerName = quote.vendedor_nombre || '';
    if (!sellerName && quote.c_vende && sellers && sellers.length > 0) {
        const found = sellers.find(s => String(s.c_vende).trim() === String(quote.c_vende).trim());
        if (found) {
            const nom = found.d_nombr || '';
            const ape = found.d_apell || '';
            sellerName = `${ape} ${nom}`.trim() || `Vendedor ${quote.c_vende}`;
        }
    }
    if (!sellerName) {
        sellerName = quote.c_usuar || '';
    }

    // Fecha
    let dateStr = '';
    const rawDate = quote.f_presu || quote.f_compr || quote.f_ingre;
    if (rawDate) {
        const parts = String(rawDate).split('T')[0].split('-');
        if (parts.length === 3) {
            dateStr = `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
    }

    // Items
    const items = quote.renglones || quote.items || [];
    const total = parseFloat(quote.i_total) || 0;
    const notes = quote.d_obser || '';

    return formatDocumentWhatsAppMessage({
        docType: 'Presupuesto',
        docNumber,
        clientName,
        date: dateStr,
        sellerName,
        companyName,
        items,
        total,
        notes
    });
};

/**
 * Función principal para despachar el envío de presupuesto por WhatsApp.
 * - Detecta dispositivos móviles/tablets para invocar la App nativa de WhatsApp.
 * - En Desktop abre WhatsApp Web directamente.
 * - Descarga el PDF localmente para que el operador pueda adjuntarlo o conservarlo.
 */
export const sendQuoteViaWhatsApp = async ({ quote, user, sellers = [] }) => {
    if (!quote) return;

    // Verificar permiso del operador
    if (user && user.l_enwhat === false) {
        Swal.fire({
            title: 'Acceso Denegado',
            text: 'Su usuario operador no tiene habilitado el permiso para realizar envíos por WhatsApp (SOPE000.L_ENWHAT).',
            icon: 'warning',
            confirmButtonColor: '#3085d6'
        });
        return;
    }

    // 1. Obtener teléfono registrado
    let rawPhone = quote.cliente_d_whatsapp || quote.d_whatsapp || quote.n_celul || quote.n_telef || '';
    let normalizedPhone = normalizeWhatsAppPhone(rawPhone);

    // 2. Si no tiene teléfono, solicitarlo mediante modal interactivo
    if (!normalizedPhone || normalizedPhone.length < 8) {
        const { value: inputPhone, isConfirmed } = await Swal.fire({
            title: 'Número de WhatsApp',
            html: `
                <p class="text-muted small mb-2">
                    El cliente <strong>${quote.d_razon || 'General'}</strong> no tiene registrado un número de WhatsApp en su ficha (MCLI000).
                </p>
                <p class="small mb-1 text-start fw-bold">Ingrese el número de celular:</p>
            `,
            input: 'text',
            inputPlaceholder: 'ej: 11 4567 8901 ó 5491145678901',
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-whatsapp me-1"></i> Abrir WhatsApp',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#25D366',
            inputValidator: (val) => {
                if (!val || !val.trim()) {
                    return 'Debe ingresar un número de teléfono.';
                }
                const norm = normalizeWhatsAppPhone(val);
                if (norm.length < 10) {
                    return 'El número ingresado parece incompleto.';
                }
                return null;
            }
        });

        if (!isConfirmed || !inputPhone) {
            return;
        }
        normalizedPhone = normalizeWhatsAppPhone(inputPhone);
    }

    // 3. Descargar PDF automáticamente para tenerlo listo
    try {
        downloadQuotePDF(quote, user, sellers);
    } catch (pdfErr) {
        console.error("Error al descargar PDF al abrir WhatsApp:", pdfErr);
    }

    // 4. Generar mensaje de WhatsApp
    const messageText = formatQuoteWhatsAppMessage(quote, user, sellers);

    // 5. Detectar móvil/tablet vs desktop
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // En móviles usamos api.whatsapp.com (abre la app nativa instalada)
    // En desktop usamos web.whatsapp.com (abre directo la sesión web)
    const whatsappBaseUrl = isMobileOrTablet
        ? `https://api.whatsapp.com/send?phone=${normalizedPhone}&text=${encodeURIComponent(messageText)}`
        : `https://web.whatsapp.com/send?phone=${normalizedPhone}&text=${encodeURIComponent(messageText)}`;

    // 6. Notificación visual informativa
    Swal.fire({
        title: '¡WhatsApp Abierto!',
        html: `
            <p class="mb-2">Se abrió la conversación de WhatsApp con el presupuesto detallado.</p>
            <p class="small text-muted mb-0">
                <i class="bi bi-file-earmark-pdf text-danger me-1"></i> 
                El archivo <strong>PDF</strong> se descargó en su equipo por si desea arrastrarlo o adjuntarlo a la conversación.
            </p>
        `,
        icon: 'success',
        confirmButtonColor: '#25D366',
        confirmButtonText: 'Entendido',
        timer: 4000,
        timerProgressBar: true
    });

    // 7. Abrir WhatsApp
    window.open(whatsappBaseUrl, '_blank');
};
